import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { componentLayoutWidths, componentPlacements } from '../data/components';
import { profileThemes } from '../data/profileThemes';
import { visibilityOptions } from '../data/widgets';
import {
  deleteUserComponent,
  installComponentTemplate,
  listComponentTemplates,
  listUserComponents,
  migrateLegacyProfileToComponents,
  publishComponentTemplate,
  updateUserComponent,
} from '../services/componentService';
import { ensureUserProfile, saveUserProfile } from '../services/profileService';
import { listUserWidgets } from '../services/widgetService';
import { defaultProfile } from '../utils/profileDefaults';

const panelOptions = [
  ['theme', 'Theme'],
  ['background', 'Background'],
  ['tiles', 'Tiles'],
  ['components', 'Components'],
  ['identity', 'Identity'],
  ['advanced', 'Advanced'],
];

const emptyPublishDraft = {
  name: '',
  description: '',
  category: 'profile',
  nsfw: false,
  adult: false,
};

function isProfileTemplate(template) {
  return template.kind === 'profile' || template.kind === 'data-view' || template.placement === 'profile' || template.placement === 'both';
}

function isProfileComponent(component) {
  return component.placement === 'profile' || component.placement === 'both';
}

function schemaFromSettings(settings = {}) {
  return {
    fields: Object.keys(settings).map((name) => ({
      name,
      type: typeof settings[name] === 'number' ? 'number' : typeof settings[name] === 'boolean' ? 'boolean' : 'markdown',
      label: name,
    })),
  };
}

function Canvas() {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(defaultProfile);
  const [templates, setTemplates] = useState([]);
  const [components, setComponents] = useState([]);
  const [activePanel, setActivePanel] = useState('identity');
  const [editing, setEditing] = useState(null);
  const [publishDraft, setPublishDraft] = useState(emptyPublishDraft);
  const [includeAdult, setIncludeAdult] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadCanvas = useCallback(async () => {
    if (!currentUser) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const [loadedProfile, loadedTemplates, loadedComponents, loadedWidgets] = await Promise.all([
        ensureUserProfile(currentUser),
        listComponentTemplates({ includeAdult }),
        listUserComponents(currentUser.uid),
        listUserWidgets(currentUser.uid),
      ]);
      let nextComponents = loadedComponents;
      const profileComponents = loadedComponents.filter(isProfileComponent);
      if (profileComponents.length === 0) {
        const migratedCount = await migrateLegacyProfileToComponents(currentUser.uid, loadedProfile, loadedWidgets);
        if (migratedCount > 0) {
          nextComponents = await listUserComponents(currentUser.uid);
          setMessage(`${migratedCount} legacy profile blocks moved into Site Theme.`);
        }
      }
      setProfile(loadedProfile);
      setTemplates(loadedTemplates.filter(isProfileTemplate));
      setComponents(nextComponents.filter(isProfileComponent));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser, includeAdult]);

  useEffect(() => {
    loadCanvas();
  }, [loadCanvas]);

  function updateProfile(field, value) {
    setProfile((current) => ({ ...current, [field]: value }));
  }

  function updateTheme(field, value) {
    setProfile((current) => ({
      ...current,
      theme: { ...current.theme, [field]: value },
    }));
  }

  function editComponent(component) {
    const template = templates.find((item) => item.id === component.templateId);
    setActivePanel('components');
    setEditing({
      ...component,
      settings: { ...(component.settings || {}) },
    });
    setPublishDraft({
      name: component.title || template?.name || '',
      description: template?.description || '',
      category: template?.category || 'profile',
      nsfw: Boolean(template?.nsfw),
      adult: Boolean(template?.adult),
    });
  }

  function updateEditing(field, value) {
    setEditing((current) => ({ ...current, [field]: value }));
  }

  function updateSetting(field, value) {
    setEditing((current) => ({
      ...current,
      settings: { ...current.settings, [field]: value },
    }));
  }

  function updatePublishDraft(field, value) {
    setPublishDraft((current) => ({ ...current, [field]: value }));
  }

  function settingFieldsFor(component) {
    const template = templates.find((item) => item.id === component.templateId);
    const schemaFields = template?.schema?.fields || [];
    if (schemaFields.length > 0) {
      return schemaFields;
    }
    return schemaFromSettings(component.settings).fields;
  }

  function renderSettingInput(field) {
    const value = editing.settings?.[field.name];
    const label = field.label || field.name;

    if (field.type === 'boolean') {
      return (
        <label className="checkbox-label" key={field.name}>
          <input type="checkbox" checked={Boolean(value)} onChange={(event) => updateSetting(field.name, event.target.checked)} />
          {label}
        </label>
      );
    }

    if (field.type === 'number') {
      return (
        <label key={field.name}>{label}<input type="number" value={value ?? ''} onChange={(event) => updateSetting(field.name, Number(event.target.value) || 0)} /></label>
      );
    }

    if (field.type === 'markdown') {
      return (
        <label key={field.name}>{label}<textarea rows="5" value={String(value ?? '')} onChange={(event) => updateSetting(field.name, event.target.value)} /></label>
      );
    }

    return (
      <label key={field.name}>{label}<input value={String(value ?? '')} onChange={(event) => updateSetting(field.name, event.target.value)} /></label>
    );
  }

  async function handleSaveProfile(event) {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const savedProfile = await saveUserProfile(currentUser.uid, profile);
      setProfile(savedProfile);
      setMessage('Site theme settings saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleInstall(template) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await installComponentTemplate(currentUser.uid, template, components.length);
      setMessage(`${template.name} added to your site theme.`);
      await loadCanvas();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdateComponent(event) {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      if (!editing.title.trim()) {
        throw new Error('Component title is required.');
      }
      await updateUserComponent(currentUser.uid, editing.id, editing);
      setMessage('Site theme component saved.');
      setEditing(null);
      await loadCanvas();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleMoveComponent(componentId, direction) {
    const index = components.findIndex((component) => component.id === componentId);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= components.length) {
      return;
    }

    setSaving(true);
    setMessage('');
    setError('');
    try {
      const reordered = [...components];
      [reordered[index], reordered[nextIndex]] = [reordered[nextIndex], reordered[index]];
      await Promise.all(reordered.map((component, position) => updateUserComponent(currentUser.uid, component.id, {
        ...component,
        position,
      })));
      setMessage('Site theme order updated.');
      await loadCanvas();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handlePublishComponent() {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      if (!editing) {
        throw new Error('Choose a component first.');
      }
      if (!publishDraft.name.trim()) {
        throw new Error('Publish name is required.');
      }
      const template = templates.find((item) => item.id === editing.templateId);
      await publishComponentTemplate(currentUser.uid, {
        id: `${editing.templateId}-${Date.now()}`,
        name: publishDraft.name.trim(),
        description: publishDraft.description.trim() || `A shared component based on ${editing.title}.`,
        kind: editing.kind,
        category: publishDraft.category.trim() || template?.category || 'profile',
        schema: template?.schema || schemaFromSettings(editing.settings),
        defaultSettings: editing.settings || {},
        defaultVisibility: editing.visibility,
        defaultLayoutWidth: editing.layoutWidth || 'half',
        nsfw: publishDraft.nsfw,
        adult: publishDraft.adult,
      });
      setMessage('Component published to the marketplace.');
      await loadCanvas();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteComponent(componentId) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await deleteUserComponent(currentUser.uid, componentId);
      setMessage('Site theme component removed.');
      if (editing?.id === componentId) {
        setEditing(null);
      }
      await loadCanvas();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const publicProfileHref = profile.username ? `/u/${profile.username}` : '/dashboard/site-theme';

  return (
    <>
      <SEO title="Site Theme" />
      <section className="page-heading canvas-heading">
        <p className="eyebrow">Site theme</p>
        <h1>Change the website theme to be your perfect design.</h1>
        <p>Change your canvas here. Start with defaults, then adjust identity, theme, backgrounds, tiles, and profile blocks when you want more control.</p>
        <div className="button-row">
          <Link className="button" to={publicProfileHref}>View profile</Link>
          <Link className="button button-secondary" to="/marketplace">Browse templates</Link>
        </div>
      </section>

      {error && <p className="error-text">{error}</p>}
      {message && <p className="notice">{message}</p>}

      <section className="surface-card canvas-switcher" aria-label="Site theme sections">
        {panelOptions.map(([id, label]) => (
          <button
            className={`tab-button${activePanel === id ? ' active' : ''}`}
            key={id}
            type="button"
            onClick={() => setActivePanel(id)}
          >
            {label}
          </button>
        ))}
      </section>

      {loading ? <p className="surface-card">Loading site theme...</p> : null}

      {!loading && activePanel === 'identity' ? (
        <section className="editor-layout">
          <form className="surface-card stacked-form" onSubmit={handleSaveProfile}>
            <h2>Identity</h2>
            <label>Display name<input value={profile.displayName} onChange={(event) => updateProfile('displayName', event.target.value)} /></label>
            <label>Username<input value={profile.username} onChange={(event) => updateProfile('username', event.target.value)} /></label>
            <label>Profile visibility
              <select value={profile.visibility} onChange={(event) => updateProfile('visibility', event.target.value)}>
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
            </label>
            <label>Tagline<input value={profile.tagline} onChange={(event) => updateProfile('tagline', event.target.value)} /></label>
            <label>Short bio<textarea value={profile.bio} rows="5" onChange={(event) => updateProfile('bio', event.target.value)} /></label>
            <button className="button" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save identity'}</button>
          </form>
          <article className="surface-card canvas-note">
            <p className="tag">Blank canvas</p>
            <h2>Start small, then add detail.</h2>
            <p>Identity sets the profile header. Components define the actual blocks visitors see below it.</p>
            <button className="button button-secondary" type="button" onClick={() => setActivePanel('components')}>Add profile components</button>
          </article>
        </section>
      ) : null}

      {!loading && activePanel === 'theme' ? (
        <section className="editor-layout">
          <form className="surface-card stacked-form" onSubmit={handleSaveProfile}>
            <h2>Theme</h2>
            <label>Profile theme
              <select value={profile.theme.template} onChange={(event) => updateTheme('template', event.target.value)}>
                {profileThemes.map((theme) => <option value={theme.id} key={theme.id}>{theme.name}</option>)}
              </select>
            </label>
            <label>Accent color<input type="color" value={profile.theme.accentColor} onChange={(event) => updateTheme('accentColor', event.target.value)} /></label>
            <button className="button" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save theme'}</button>
          </form>
          <div className="surface-card">
            <h2>Theme presets</h2>
            <div className="theme-grid">
              {profileThemes.map((theme) => (
                <button
                  className={`theme-card${profile.theme.template === theme.id ? ' active' : ''}`}
                  key={theme.id}
                  type="button"
                  onClick={() => {
                    updateTheme('template', theme.id);
                    updateTheme('accentColor', theme.accentColor);
                  }}
                >
                  <span className="theme-swatch" style={{ backgroundColor: theme.accentColor }} />
                  <strong>{theme.name}</strong>
                  <span>{theme.description}</span>
                </button>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {!loading && activePanel === 'background' ? (
        <section className="editor-layout">
          <form className="surface-card stacked-form" onSubmit={handleSaveProfile}>
            <h2>Background</h2>
            <label>Background style
              <select value={profile.theme.backgroundStyle || 'plain'} onChange={(event) => updateTheme('backgroundStyle', event.target.value)}>
                <option value="plain">Plain</option>
                <option value="fade">Fade</option>
                <option value="gradient">Gradient</option>
                <option value="filled">Filled</option>
                <option value="default-picture">Default picture</option>
                <option value="uploaded-picture">Uploaded picture placeholder</option>
                <option value="public-search">Public picture search placeholder</option>
                <option value="ai-generated">AI generated placeholder</option>
              </select>
            </label>
            <label>Background color<input type="color" value={profile.theme.backgroundColor || '#f7f2ea'} onChange={(event) => updateTheme('backgroundColor', event.target.value)} /></label>
            <button className="button" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save background'}</button>
          </form>
          <article className="surface-card canvas-note">
            <p className="tag">Simple now</p>
            <h2>More sources later.</h2>
            <p>Uploads, public picture search, and AI images are prepared as design options, but real upload/generation comes later.</p>
          </article>
        </section>
      ) : null}

      {!loading && activePanel === 'tiles' ? (
        <section className="editor-layout">
          <form className="surface-card stacked-form" onSubmit={handleSaveProfile}>
            <h2>Tile defaults</h2>
            <label>Tile scale
              <select value={profile.theme.tileScale || 'normal'} onChange={(event) => updateTheme('tileScale', event.target.value)}>
                <option value="compact">Compact</option>
                <option value="normal">Normal</option>
                <option value="detailed">Detailed</option>
                <option value="large">Large</option>
              </select>
            </label>
            <label>Tile opacity<input type="range" min="55" max="100" value={profile.theme.tileOpacity || 90} onChange={(event) => updateTheme('tileOpacity', Number(event.target.value))} /></label>
            <button className="button" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save tile defaults'}</button>
          </form>
          <article className="surface-card canvas-note">
            <p className="tag">Customizable by tile</p>
            <h2>Defaults first, individual controls later.</h2>
            <p>Dashboard and landing stay simple. Individual tile controls live behind corner settings buttons.</p>
          </article>
        </section>
      ) : null}

      {!loading && activePanel === 'components' ? (
        <section className="editor-layout">
          <div className="surface-card">
            <div className="section-toolbar">
              <div>
                <h2>Profile components</h2>
                <p className="muted">These blocks appear on your public profile when their visibility allows it.</p>
              </div>
              <label className="inline-toggle">
                <input type="checkbox" checked={includeAdult} onChange={(event) => setIncludeAdult(event.target.checked)} />
                Show 18+
              </label>
            </div>
            {components.length === 0 ? (
              <div className="empty-state">
                <h3>Your site theme has no profile blocks yet.</h3>
                <p>Add Hero Bio or Custom Markdown to start building a standalone profile.</p>
              </div>
            ) : null}
            <div className="item-list">
              {components.map((component, index) => (
                <article className="content-item" key={component.id}>
                  <p className="tag">{component.kind} · {component.visibility} · {component.placement} · {component.layoutWidth || 'half'}</p>
                  <h3>{component.title}</h3>
                  <p className="muted">Position {component.position || 0}</p>
                  <div className="button-row compact-row">
                    <button className="button button-secondary" type="button" disabled={saving || index === 0} onClick={() => handleMoveComponent(component.id, -1)}>Up</button>
                    <button className="button button-secondary" type="button" disabled={saving || index === components.length - 1} onClick={() => handleMoveComponent(component.id, 1)}>Down</button>
                    <button className="button button-secondary" type="button" onClick={() => editComponent(component)}>Configure</button>
                    <button className="button button-secondary" type="button" disabled={saving} onClick={() => handleDeleteComponent(component.id)}>Remove</button>
                  </div>
                </article>
              ))}
            </div>
          </div>
          <div className="surface-card">
            {editing ? (
              <form className="stacked-form" onSubmit={handleUpdateComponent}>
                <h2>Configure component</h2>
                <label>Title<input value={editing.title} onChange={(event) => updateEditing('title', event.target.value)} /></label>
                <label>Position<input type="number" value={editing.position || 0} onChange={(event) => updateEditing('position', event.target.value)} /></label>
                <label>Visibility
                  <select value={editing.visibility} onChange={(event) => updateEditing('visibility', event.target.value)}>
                    {visibilityOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
                  </select>
                </label>
                <label>Placement
                  <select value={editing.placement} onChange={(event) => updateEditing('placement', event.target.value)}>
                    {componentPlacements.map((placement) => <option value={placement} key={placement}>{placement}</option>)}
                  </select>
                </label>
                <label>Layout width
                  <select value={editing.layoutWidth || 'half'} onChange={(event) => updateEditing('layoutWidth', event.target.value)}>
                    {componentLayoutWidths.map((layoutWidth) => <option value={layoutWidth} key={layoutWidth}>{layoutWidth}</option>)}
                  </select>
                </label>
                <h3>Settings</h3>
                {settingFieldsFor(editing).map((field) => renderSettingInput(field))}
                <div className="button-row compact-row">
                  <button className="button" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save component'}</button>
                  <button className="button button-secondary" type="button" onClick={() => setEditing(null)}>Cancel</button>
                </div>
                <div className="publish-panel">
                  <h3>Publish template</h3>
                  <p className="muted">Share this component's schema and default settings in the marketplace.</p>
                  <label>Publish name<input value={publishDraft.name} onChange={(event) => updatePublishDraft('name', event.target.value)} /></label>
                  <label>Description<textarea rows="3" value={publishDraft.description} onChange={(event) => updatePublishDraft('description', event.target.value)} /></label>
                  <label>Category<input value={publishDraft.category} onChange={(event) => updatePublishDraft('category', event.target.value)} /></label>
                  <label className="checkbox-label"><input type="checkbox" checked={publishDraft.nsfw} onChange={(event) => updatePublishDraft('nsfw', event.target.checked)} /> Mark NSFW</label>
                  <label className="checkbox-label"><input type="checkbox" checked={publishDraft.adult} onChange={(event) => updatePublishDraft('adult', event.target.checked)} /> Mark 18+</label>
                  <button className="button button-secondary" type="button" disabled={saving} onClick={handlePublishComponent}>Publish to marketplace</button>
                </div>
              </form>
            ) : (
              <>
                <h2>Add component</h2>
                <div className="item-list">
                  {templates.map((template) => (
                    <article className="content-item" key={template.id}>
                      <p className="tag">{template.kind} · {template.category}</p>
                      <h3>{template.name}</h3>
                      <p>{template.description}</p>
                      <p className="muted">Default: {template.defaultVisibility} · {template.placement}</p>
                      <button className="button button-secondary" type="button" disabled={saving} onClick={() => handleInstall(template)}>Add to site theme</button>
                    </article>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      ) : null}

      {!loading && activePanel === 'advanced' ? (
        <section className="surface-card">
          <h2>Advanced customization backlog</h2>
          <ul className="clean-list">
            <li>Resize tiles by dragging edges.</li>
            <li>Upload personal background pictures.</li>
            <li>Search public image sources.</li>
            <li>Generate optional AI backgrounds.</li>
            <li>Save per-tile color, opacity, and detail level.</li>
          </ul>
        </section>
      ) : null}
    </>
  );
}

export default Canvas;
